import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import OutsourcePOForm, { type OutsourcePOFormData } from "./OutsourcePOForm";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { Loader2 } from "lucide-react";

export default function OutsourceOrderEdit() {
  const navigate = useNavigate();
  const { orderId } = useParams({ strict: false }) as { orderId: string };
  const queryClient = useQueryClient();

  const { data: po, isLoading } = useQuery({
    queryKey: ["outsource-po", orderId],
    queryFn: async () => trpcClient.outsourcePos.getById.query({ id: orderId }),
    enabled: !!orderId,
  });

  const handleUpdate = async (formData: OutsourcePOFormData) => {
    try {
      const { items, filterDetailIds, ...header } = formData;
      const totalQty = items.reduce((s, it) => s + (it.quantity || 0), 0);
      // Backend tự tính totalAmount
      // Nhiều dòng cùng quy cách: chỉ ghi một lần, không lặp lại (FB-O9IDAX)
      const uniq = (vals: (string | undefined | null)[]) =>
        Array.from(new Set(vals.map((v) => (v || "").trim()).filter(Boolean)));
      const productNames = uniq(items.map((it) => it.productName)).join(", ");
      const productSpecs = uniq(items.map((it) => it.productSpecs)).join("; ");

      const dataToUpdate = {
        orderId: header.orderId || undefined,
        // Trước đây không gửi LSX / công đoạn → chọn xong lưu vẫn mất (FB-9NDFJY)
        productionOrderId: header.productionOrderId || null,
        routingStepIds: header.routingStepIds || [],
        vendorId: header.vendorId,
        deploymentModel: header.deploymentModel || "OUTSOURCE_100",
        productName: productNames,
        productSpecs: productSpecs,
        // Trước đây không gửi ghi chú → gõ Ghi chú cho NCC rồi lưu vẫn mất
        notes: header.notes?.trim() || null,
        qtyOrdered: totalQty,
        unitPrice: items.length === 1 ? items[0].unitPrice : 0,
        issueDate: header.issueDate,
        expectedDate: header.expectedDate,
        status: header.status || "DRAFT",
        saveMode: (header as any).saveMode,
        materialIssued: items.map((it) => ({
          productId: it.productId,
          productName: it.productName,
          productSpecs: it.productSpecs,
          quantity: it.quantity,
          unit: it.unit || undefined,
          unitPrice: it.unitPrice,
          amount: it.amount,
          notes: it.notes,
        })),
      };

      await trpcClient.outsourcePos.update.mutate({
        id: orderId,
        data: dataToUpdate,
      });
      toast.success("Cập nhật Đơn Gia công thành công!");
      await queryClient.invalidateQueries({ queryKey: ["outsource-pos"] });
      await queryClient.invalidateQueries({ queryKey: ["outsource-po", orderId] });
      navigate({
        to: "/outsource-orders/$orderId",
        params: { orderId },
        replace: true,
      });
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi cập nhật đơn gia công");
    }
  };

  if (isLoading) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Đơn Gia công", to: "/outsource-orders" }]}
        title="Đang tải..."
      >
        <div className="flex items-center justify-center h-48">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
        </div>
      </StandardDetailLayout>
    );
  }

  if (!po) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Đơn Gia công", to: "/outsource-orders" }]}
        title="Không tìm thấy"
      >
        <div className="flex items-center justify-center h-48 text-red-500">
          Không tìm thấy đơn gia công này.
        </div>
      </StandardDetailLayout>
    );
  }

  // Pre-fill fields from po
  // Handle items from materialIssued because materialIssued is a JSON array
  let initialItems = [];
  try {
    if (typeof po.materialIssued === "string") {
      initialItems = JSON.parse(po.materialIssued as string);
    } else if (Array.isArray(po.materialIssued)) {
      initialItems = po.materialIssued as any;
    }
  } catch (e) {
    console.error("Failed to parse materialIssued", e);
  }

  const defaultValues: Partial<OutsourcePOFormData> = {
    vendorId: po.vendorId || "",
    orderId: po.orderId || "",
    issueDate: po.issueDate?.toISOString() || new Date().toISOString(),
    expectedDate: po.expectedDate?.toISOString() || new Date().toISOString(),
    status: po.status || "DRAFT",
    notes: (po as any).notes || "",
    deploymentModel: po.deploymentModel || "OUTSOURCE_100",
    productionOrderId: (po as any).productionOrderId || "",
    routingStepIds: (po as any).routingStepIds?.length
      ? (po as any).routingStepIds
      : (po as any).routingStepId ? [(po as any).routingStepId] : [],
    items: initialItems,
  };

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Đơn Gia công", to: "/outsource-orders" },
        {
          label: po?.poId || orderId,
          to: "/outsource-orders/$orderId",
          params: { orderId },
        },
        { label: "Chỉnh sửa" },
      ]}
      title={`Chỉnh sửa ${po?.poId || orderId}`}
    >
      <div className="bg-transparent border-0 p-0 shadow-none">
        <OutsourcePOForm
          isEdit
          defaultValues={defaultValues}
          onSubmit={handleUpdate}
          onCancel={() =>
            navigate({ to: "/outsource-orders/$orderId", params: { orderId } })
          }
          submitLabel={po?.status === "DRAFT" ? "Lưu" : "Lưu thay đổi"}
        />
      </div>
    </StandardDetailLayout>
  );
}
